/**
 * PURPOSE: Filtros compartidos y proyecciones por vista del Centro de Programación (FTR-0028 WG3).
 * RESPONSIBILITIES: Un solo filtrado para las cuatro vistas y un agrupador por vista. Puro: entra un
 *   array de `ProgrammingMatchViewModel`, sale una estructura de presentación. Sin React y sin red.
 *
 *   D-23 — UNA SOLA FUENTE. Las cuatro vistas NO tienen cuatro estados: comparten `rows` y `filters`
 *   y solo cambian de PROYECCIÓN. Cambiar de vista no vuelve a filtrar con otras reglas ni pierde la
 *   selección, porque el filtrado vive aquí y se aplica una vez.
 *
 *   C9. Agrupar "por fecha" agrupa por `date_no`, que es la jornada de la PLANTILLA del grupo, no una
 *   fecha del calendario. Dos grupos de distinto tamaño pueden estar en rondas distintas bajo el
 *   mismo `date_no`, así que cada fila lleva su `leg` y la vista lo muestra. La vista Calendario, en
 *   cambio, agrupa por `date_match`, que sí es una fecha real.
 * COLLABORATORS: programmingCenterModel.js, GroupView/DateView/TimelineView/VenueView.
 * ROUTE: winscore/src/utils/programmingCenterGrouping.js
 */
import { ROW_STATUS, dateLabel, groupLetter } from "./programmingCenterModel.js";

export const EMPTY_FILTERS = Object.freeze({
  groupNo: "",
  dateNo: "",
  idsede: "",
  idcancha: "",
  status: "",
  phase: "",
  search: "",
  showOrphans: false,
});

/** Estados ofrecidos en el filtro. `orphan` solo aparece si se pide verlos. */
export const STATUS_FILTERS = Object.freeze([
  { value: "", label: "Todos los estados" },
  { value: "draft", label: "Borrador" },
  { value: "materialized", label: "Materializado" },
  { value: "scheduled", label: "Programado" },
  { value: "unscheduled", label: "Sin programar" },
  { value: "pendingParticipants", label: "Sin participantes" },
  { value: "officialized", label: "Oficializado" },
  { value: "bye", label: "BYE" },
  { value: "orphan", label: "Huérfano" },
]);

const normalize = (value) =>
  String(value || "")
    .toLocaleLowerCase("es-PE")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const matchesSearch = (row, needle) => {
  if (!needle) return true;
  const haystack = normalize(
    [
      row.draftKey,
      row.label,
      row.groupLabel,
      row.tagFinal,
      row.venueName,
      row.courtName,
      row.home?.slotLabel,
      row.home?.participantName,
      row.away?.slotLabel,
      row.away?.participantName,
      row.nroMatch ? `#${row.nroMatch}` : null,
    ]
      .filter(Boolean)
      .join(" ")
  );
  return haystack.includes(normalize(needle));
};

const matchesStatus = (row, status) => {
  switch (status) {
    case "":
      return true;
    case "draft":
      return !row.isMaterialized && !row.isOrphan;
    case "materialized":
      return row.isMaterialized;
    case "scheduled":
      return row.isScheduled;
    case "unscheduled":
      return !row.isBye && !row.isScheduled;
    case "pendingParticipants":
      return row.participantsPending;
    case "officialized":
      return row.isOfficialized;
    case "bye":
      return row.isBye;
    case "orphan":
      return row.isOrphan;
    default:
      return true;
  }
};

/**
 * El filtrado compartido. Las huérfanas quedan FUERA por defecto (son estructura retirada que
 * conserva su programación para un posible regreso) salvo que se pidan explícitamente o se filtre
 * por ese estado: ocultarlas en silencio y sin salida sería esconder información operativa.
 */
export const filterRows = (rows = [], filters = EMPTY_FILTERS) => {
  const wantsOrphans = filters.showOrphans === true || filters.status === "orphan";
  return rows.filter((row) => {
    if (row.isOrphan && !wantsOrphans) return false;
    if (row.isDeleted) return false;
    if (filters.groupNo !== "" && Number(row.groupNo) !== Number(filters.groupNo)) return false;
    if (filters.dateNo !== "" && Number(row.dateNo) !== Number(filters.dateNo)) return false;
    if (filters.idsede !== "" && Number(row.idsede) !== Number(filters.idsede)) return false;
    if (filters.idcancha !== "" && Number(row.idcancha) !== Number(filters.idcancha)) return false;
    if (filters.phase !== "" && Number(row.idtypephase) !== Number(filters.phase)) return false;
    if (!matchesStatus(row, filters.status || "")) return false;
    if (!matchesSearch(row, filters.search)) return false;
    return true;
  });
};

export const hasActiveFilters = (filters = EMPTY_FILTERS) =>
  Object.keys(EMPTY_FILTERS).some((key) => filters[key] !== EMPTY_FILTERS[key]);

/** Opciones que el filtro ofrece, derivadas de las filas REALES (nunca de un rango inventado). */
export const buildFilterOptions = (rows = [], context = null) => {
  const groups = new Map();
  const dates = new Map();
  for (const row of rows) {
    if (row.groupNo) groups.set(Number(row.groupNo), `Grupo ${groupLetter(row.groupNo)}`);
    if (row.dateNo) {
      const key = `${row.idtypephase}:${row.dateNo}`;
      dates.set(key, {
        value: Number(row.dateNo),
        phase: Number(row.idtypephase),
        label: dateLabel(row.dateNo, { idtypephase: row.idtypephase }),
      });
    }
  }
  const venues = (context?.venues || []).map((venue) => ({
    value: Number(venue.idsede),
    label: venue.name,
    courts: (venue.courts || []).map((court) => ({
      value: Number(court.idcancha),
      label: court.name,
      retired: Number(court.state) !== 1,
    })),
  }));

  return {
    groups: [...groups.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([value, label]) => ({ value, label })),
    dates: [...dates.values()].sort((a, b) => a.phase - b.phase || a.value - b.value),
    venues,
  };
};

// ------------------------------------- proyecciones por vista -------------------------------------

/**
 * VISTA 1 — Por Grupo. Estructura deportiva: grupo → jornada → partidos, y las fases finales como un
 * bloque aparte con sus etapas. Es la vista que responde "qué está definido", no "cuándo se juega".
 */
export const groupByGroup = (rows = []) => {
  const groups = new Map();
  const finals = new Map();
  const loose = [];

  for (const row of rows) {
    if (Number(row.idtypephase) === 2) {
      const stage = row.stage || row.tagFinal || "Fase final";
      if (!finals.has(stage)) finals.set(stage, { stage, dateNo: row.dateNo, rows: [] });
      finals.get(stage).rows.push(row);
      continue;
    }
    if (!row.groupNo) {
      loose.push(row);
      continue;
    }
    const key = Number(row.groupNo);
    if (!groups.has(key)) {
      groups.set(key, {
        groupNo: key,
        letter: groupLetter(key),
        label: `Grupo ${groupLetter(key)}`,
        dates: new Map(),
        rows: [],
      });
    }
    const group = groups.get(key);
    group.rows.push(row);
    const dateKey = Number(row.dateNo) || 0;
    if (!group.dates.has(dateKey)) {
      group.dates.set(dateKey, { dateNo: dateKey, leg: row.leg, rows: [] });
    }
    group.dates.get(dateKey).rows.push(row);
  }

  return {
    groups: [...groups.values()]
      .sort((a, b) => a.groupNo - b.groupNo)
      .map((group) => ({
        ...group,
        dates: [...group.dates.values()].sort((a, b) => a.dateNo - b.dateNo),
      })),
    finals: [...finals.values()].sort((a, b) => (a.dateNo || 0) - (b.dateNo || 0)),
    loose,
  };
};

/**
 * VISTA 2 — Por Fecha. Operación por jornada: `date_no` → grupos. Cada bloque lleva las rondas que
 * conviven en esa jornada, porque con grupos de distinto tamaño no son la misma (C9).
 */
export const groupByDate = (rows = []) => {
  const dates = new Map();
  for (const row of rows) {
    const key = `${Number(row.idtypephase) || 1}:${Number(row.dateNo) || 0}`;
    if (!dates.has(key)) {
      dates.set(key, {
        key,
        dateNo: Number(row.dateNo) || 0,
        phase: Number(row.idtypephase) || 1,
        label: dateLabel(row.dateNo, { idtypephase: row.idtypephase }),
        legs: new Set(),
        rows: [],
      });
    }
    const bucket = dates.get(key);
    bucket.rows.push(row);
    if (row.leg) bucket.legs.add(Number(row.leg));
  }

  return [...dates.values()]
    .sort((a, b) => a.phase - b.phase || a.dateNo - b.dateNo)
    .map((bucket) => ({
      ...bucket,
      legs: [...bucket.legs].sort((a, b) => a - b),
      // Con más de una ronda en la misma jornada, la vista debe decirlo en vez de dar a entender
      // que `nro_fecha` y ronda son lo mismo.
      mixedLegs: bucket.legs.size > 1,
      groups: [...groupsOf(bucket.rows)],
    }));
};

const groupsOf = (rows) => {
  const groups = new Map();
  for (const row of rows) {
    const key = row.groupNo ? Number(row.groupNo) : 0;
    if (!groups.has(key)) {
      groups.set(key, {
        groupNo: key || null,
        label: key ? `Grupo ${groupLetter(key)}` : "Sin grupo",
        rows: [],
      });
    }
    groups.get(key).rows.push(row);
  }
  return [...groups.values()].sort((a, b) => (a.groupNo || 99) - (b.groupNo || 99));
};

const UNASSIGNED_VENUE = Object.freeze({ idsede: null, name: "Sin sede asignada" });

/**
 * VISTA 4 — Por Sede / Tabla de Programación. Replica la jerarquía de la Programación existente:
 * sede → fecha del calendario → partidos ordenados por hora y `nro_match`. Los BYE quedan fuera:
 * nunca se juegan en una cancha (D-20).
 */
export const groupByVenue = (rows = [], context = null) => {
  const venuesById = new Map((context?.venues || []).map((venue) => [Number(venue.idsede), venue]));
  const venues = new Map();

  for (const row of rows) {
    if (row.isBye) continue;
    const key = row.idsede ? Number(row.idsede) : 0;
    if (!venues.has(key)) {
      const venue = key ? venuesById.get(key) : null;
      venues.set(key, {
        idsede: key || null,
        name: venue?.name || (key ? `Sede ${key}` : UNASSIGNED_VENUE.name),
        address: venue?.address || null,
        dates: new Map(),
        rows: [],
      });
    }
    const bucket = venues.get(key);
    bucket.rows.push(row);
    const dateKey = row.date || "";
    if (!bucket.dates.has(dateKey)) {
      bucket.dates.set(dateKey, { date: dateKey || null, rows: [] });
    }
    bucket.dates.get(dateKey).rows.push(row);
  }

  return [...venues.values()]
    // "Sin sede asignada" siempre al final: es una bandeja de pendientes, no una sede.
    .sort((a, b) => {
      if (!a.idsede) return 1;
      if (!b.idsede) return -1;
      return String(a.name).localeCompare(String(b.name), "es-PE");
    })
    .map((venue) => ({
      ...venue,
      dates: [...venue.dates.values()]
        .sort((a, b) => String(a.date || "9999").localeCompare(String(b.date || "9999")))
        .map((bucket) => ({
          ...bucket,
          rows: [...bucket.rows].sort(
            (a, b) =>
              String(a.time || "99:99").localeCompare(String(b.time || "99:99")) ||
              (a.nroMatch || 0) - (b.nroMatch || 0) ||
              a.sortOrder - b.sortOrder
          ),
        })),
    }));
};

const timeToMinutes = (time) => {
  const parsed = /^(\d{1,2}):(\d{2})/.exec(String(time || ""));
  if (!parsed) return null;
  return Number(parsed[1]) * 60 + Number(parsed[2]);
};

export const minutesToTime = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

/**
 * VISTA 3 — Calendario / Timeline. Un DÍA real a la vez (`date_match`), carriles = cancha agrupada
 * por sede, filas = franjas horarias. Lo que no tiene día, hora o sede va a la bandeja "Sin
 * programar": no se le inventa una posición en la rejilla.
 */
export const buildTimeline = (rows = [], { slotMinutes = 60, stepMinutes = 30, date = null } = {}) => {
  const schedulable = rows.filter((row) => !row.isBye && !row.isOrphan);
  const days = [...new Set(schedulable.map((row) => row.date).filter(Boolean))].sort();
  const activeDate = date && days.includes(date) ? date : days[0] || null;

  const unscheduled = schedulable.filter(
    (row) => !row.date || !row.time || !row.idsede
  );
  const placed = schedulable.filter(
    (row) => row.date === activeDate && row.time && row.idsede
  );

  const lanes = new Map();
  for (const row of placed) {
    const key = `${row.idsede}|${row.idcancha || 0}`;
    if (!lanes.has(key)) {
      lanes.set(key, {
        key,
        idsede: Number(row.idsede),
        idcancha: row.idcancha ? Number(row.idcancha) : null,
        venueName: row.venueName || `Sede ${row.idsede}`,
        courtName: row.courtName || "Sede",
        rows: [],
      });
    }
    lanes.get(key).rows.push(row);
  }

  const starts = placed.map((row) => timeToMinutes(row.time)).filter((value) => value !== null);
  const first = starts.length ? Math.min(...starts) : 8 * 60;
  const last = starts.length ? Math.max(...starts) + slotMinutes : 12 * 60;
  const from = Math.max(0, Math.floor((first - 60) / stepMinutes) * stepMinutes);
  const to = Math.min(24 * 60, Math.ceil((last + 60) / stepMinutes) * stepMinutes);

  const slots = [];
  for (let minute = from; minute < to; minute += stepMinutes) slots.push(minute);

  return {
    days,
    activeDate,
    slotMinutes,
    stepMinutes,
    from,
    to,
    slots,
    unscheduled,
    lanes: [...lanes.values()].sort(
      (a, b) =>
        String(a.venueName).localeCompare(String(b.venueName), "es-PE") ||
        String(a.courtName).localeCompare(String(b.courtName), "es-PE")
    ),
    placement: placed.map((row) => ({
      row,
      laneKey: `${row.idsede}|${row.idcancha || 0}`,
      start: timeToMinutes(row.time),
      end: (timeToMinutes(row.time) ?? 0) + slotMinutes,
    })),
  };
};

/**
 * Índice de conflictos por fila, a partir del bloque `timeline` que devuelve `GET /draft`. El cálculo
 * es del backend (`engine/timelineConflicts.js`); aquí solo se indexa para pintarlo.
 */
export const indexConflicts = (timeline) => {
  const byKey = new Map();
  for (const conflict of timeline?.conflicts || []) {
    for (const target of conflict.rows || []) {
      const key = target.draft_key;
      if (!key) continue;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(conflict);
    }
  }
  return byKey;
};

export const conflictSeverity = (conflicts = []) => {
  if (!conflicts.length) return null;
  return conflicts.some((conflict) => conflict.severity === "error") ? "error" : "warning";
};

export { ROW_STATUS };
